/**
 * Report Templates — Phase 14 Reporting Engine
 * Pre-built report definitions covering all regulatory and operational frequencies.
 * Each template defines: metadata, data sources, default filters, output columns, and export options.
 */

export const REPORT_FREQUENCIES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUAL: 'annual',
  AD_HOC: 'ad_hoc'
};

export const REPORT_CATEGORIES = {
  CLINICAL: 'clinical',
  FINANCIAL: 'financial',
  QUALITY: 'quality',
  HR: 'hr',
  OPERATIONS: 'operations',
  COMPLIANCE: 'compliance',
  GOVERNMENT: 'government',
  CRM: 'crm'
};

export const EXPORT_FORMATS = ['pdf', 'excel', 'csv', 'json'];

export const FIELD_CATALOG = [
  { id: 'resident_name', label: 'Resident Name', module: 'residents', type: 'text' },
  { id: 'resident_id', label: 'Resident ID', module: 'residents', type: 'text' },
  { id: 'room_number', label: 'Room Number', module: 'residents', type: 'text' },
  { id: 'wing', label: 'Wing / Ward', module: 'residents', type: 'text' },
  { id: 'admission_date', label: 'Admission Date', module: 'residents', type: 'date' },
  { id: 'anacc_class', label: 'AN-ACC Class', module: 'residents', type: 'text' },
  { id: 'care_minutes_target', label: 'Care Minutes Target', module: 'care_minutes', type: 'number' },
  { id: 'care_minutes_actual', label: 'Care Minutes Actual', module: 'care_minutes', type: 'number' },
  { id: 'rn_minutes_target', label: 'RN Minutes Target', module: 'care_minutes', type: 'number' },
  { id: 'rn_minutes_actual', label: 'RN Minutes Actual', module: 'care_minutes', type: 'number' },
  { id: 'medication_name', label: 'Medication Name', module: 'medication', type: 'text' },
  { id: 'medication_dose', label: 'Dose', module: 'medication', type: 'text' },
  { id: 'administration_time', label: 'Administration Time', module: 'medication', type: 'datetime' },
  { id: 'administered_by', label: 'Administered By', module: 'medication', type: 'text' },
  { id: 'incident_type', label: 'Incident Type', module: 'incidents', type: 'text' },
  { id: 'incident_severity', label: 'Severity', module: 'incidents', type: 'text' },
  { id: 'incident_date', label: 'Incident Date', module: 'incidents', type: 'date' },
  { id: 'incident_status', label: 'Incident Status', module: 'incidents', type: 'text' },
  { id: 'assessment_type', label: 'Assessment Type', module: 'clinical', type: 'text' },
  { id: 'assessment_score', label: 'Assessment Score', module: 'clinical', type: 'number' },
  { id: 'assessment_date', label: 'Assessment Date', module: 'clinical', type: 'date' },
  { id: 'care_plan_status', label: 'Care Plan Status', module: 'careplans', type: 'text' },
  { id: 'care_plan_goals', label: 'Active Goals', module: 'careplans', type: 'number' },
  { id: 'staff_name', label: 'Staff Name', module: 'roster', type: 'text' },
  { id: 'staff_role', label: 'Staff Role', module: 'roster', type: 'text' },
  { id: 'shift_date', label: 'Shift Date', module: 'roster', type: 'date' },
  { id: 'shift_hours', label: 'Shift Hours', module: 'roster', type: 'number' },
  { id: 'occupancy_count', label: 'Current Occupancy', module: 'facility', type: 'number' },
  { id: 'occupancy_rate', label: 'Occupancy Rate %', module: 'facility', type: 'number' },
  { id: 'revenue', label: 'Revenue', module: 'financial', type: 'currency' },
  { id: 'expenses', label: 'Expenses', module: 'financial', type: 'currency' },
  { id: 'enquiry_source', label: 'Enquiry Source', module: 'crm', type: 'text' },
  { id: 'enquiry_status', label: 'Enquiry Status', module: 'crm', type: 'text' },
  { id: 'waitlist_position', label: 'Waitlist Position', module: 'crm', type: 'number' },
  { id: 'nqip_indicator', label: 'NQIP Indicator', module: 'quality', type: 'text' },
  { id: 'nqip_value', label: 'NQIP Value', module: 'quality', type: 'number' },
  { id: 'star_rating', label: 'Star Rating', module: 'quality', type: 'number' },
  { id: 'complaint_type', label: 'Complaint Type', module: 'quality', type: 'text' },
  { id: 'complaint_status', label: 'Complaint Status', module: 'quality', type: 'text' },
  { id: 'infection_type', label: 'Infection Type', module: 'clinical', type: 'text' },
  { id: 'antimicrobial_name', label: 'Antimicrobial Name', module: 'medication', type: 'text' },
  { id: 'restrictive_practice_type', label: 'Restrictive Practice Type', module: 'clinical', type: 'text' },
  { id: 'psychotropic_medication', label: 'Psychotropic Medication', module: 'medication', type: 'text' }
];

const PREBUILT_REPORTS = [
  // ─── DAILY ────────────────────────────────────────────
  {
    id: 'daily-handover',
    name: 'Handover Report',
    description: 'Shift handover summary with resident status changes, pending tasks, and critical alerts for incoming staff.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['residents', 'incidents', 'medication', 'careplans', 'clinical'],
    defaultColumns: ['resident_name', 'room_number', 'wing', 'incident_type', 'care_plan_status'],
    exportFormats: ['pdf', 'excel'],
    icon: 'ClipboardList',
    color: 'blue'
  },
  {
    id: 'daily-medication-admin',
    name: 'Medication Administration Summary',
    description: 'Daily medication rounds: administrations, missed doses, PRN usage, and errors.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['medication', 'residents'],
    defaultColumns: ['resident_name', 'medication_name', 'medication_dose', 'administration_time', 'administered_by'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'Pill',
    color: 'red'
  },
  {
    id: 'daily-care-minutes',
    name: 'Care Minutes Dashboard',
    description: 'Real-time care minutes delivered vs target by role category and ward.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.COMPLIANCE,
    dataSources: ['care_minutes', 'roster', 'residents'],
    defaultColumns: ['wing', 'care_minutes_target', 'care_minutes_actual', 'rn_minutes_target', 'rn_minutes_actual'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Clock',
    color: 'purple'
  },
  {
    id: 'daily-occupancy',
    name: 'Occupancy Dashboard',
    description: 'Current bed occupancy, admissions, discharges, leave, and available beds by wing.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.OPERATIONS,
    dataSources: ['residents', 'facility'],
    defaultColumns: ['wing', 'occupancy_count', 'occupancy_rate'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Building2',
    color: 'teal'
  },
  {
    id: 'daily-incident-summary',
    name: 'Incident Summary',
    description: 'All incidents in the past 24 hours: falls, behavioural, medication, infection, abuse/neglect.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['incidents', 'residents'],
    defaultColumns: ['resident_name', 'incident_type', 'incident_severity', 'incident_date', 'incident_status'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'AlertTriangle',
    color: 'orange'
  },
  {
    id: 'daily-resident-movement',
    name: 'Resident Movement Board',
    description: 'Movements: hospital transfers, social leave, respite in/out, new admissions, deaths.',
    frequency: REPORT_FREQUENCIES.DAILY,
    category: REPORT_CATEGORIES.OPERATIONS,
    dataSources: ['residents', 'facility'],
    defaultColumns: ['resident_name', 'room_number', 'wing', 'admission_date'],
    exportFormats: ['pdf'],
    icon: 'ArrowLeftRight',
    color: 'indigo'
  },

  // ─── WEEKLY ───────────────────────────────────────────
  {
    id: 'weekly-clinical-review',
    name: 'Clinical Review Summary',
    description: 'Weekly clinical changes: new assessments, care plan updates, weight changes, wound status, falls trend.',
    frequency: REPORT_FREQUENCIES.WEEKLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['clinical', 'careplans', 'incidents', 'residents'],
    defaultColumns: ['resident_name', 'assessment_type', 'assessment_score', 'care_plan_status', 'care_plan_goals'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Stethoscope',
    color: 'blue'
  },
  {
    id: 'weekly-waitlist-status',
    name: 'Waitlist Status',
    description: 'CRM pipeline: enquiries, waitlist position, expected move-in dates, bed availability.',
    frequency: REPORT_FREQUENCIES.WEEKLY,
    category: REPORT_CATEGORIES.CRM,
    dataSources: ['crm', 'facility'],
    defaultColumns: ['resident_name', 'enquiry_source', 'enquiry_status', 'waitlist_position'],
    exportFormats: ['pdf', 'excel'],
    icon: 'ListOrdered',
    color: 'green'
  },
  {
    id: 'weekly-staffing-adequacy',
    name: 'Staffing Adequacy',
    description: 'Rostered vs actual staffing by role, overtime hours, unfilled shifts, agency usage.',
    frequency: REPORT_FREQUENCIES.WEEKLY,
    category: REPORT_CATEGORIES.HR,
    dataSources: ['roster'],
    defaultColumns: ['staff_role', 'shift_date', 'shift_hours'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'Users',
    color: 'purple'
  },
  {
    id: 'weekly-maintenance',
    name: 'Maintenance Requests',
    description: 'Open, in-progress, and completed maintenance requests with average resolution time.',
    frequency: REPORT_FREQUENCIES.WEEKLY,
    category: REPORT_CATEGORIES.OPERATIONS,
    dataSources: ['facility'],
    defaultColumns: [],
    exportFormats: ['pdf', 'excel'],
    icon: 'Wrench',
    color: 'gray'
  },
  {
    id: 'weekly-dietary-changes',
    name: 'Dietary Changes',
    description: 'Residents with dietary modifications this week: IDDSI level changes, new allergies, texture modifications.',
    frequency: REPORT_FREQUENCIES.WEEKLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['residents', 'clinical'],
    defaultColumns: ['resident_name', 'room_number', 'wing'],
    exportFormats: ['pdf'],
    icon: 'UtensilsCrossed',
    color: 'amber'
  },

  // ─── MONTHLY ──────────────────────────────────────────
  {
    id: 'monthly-care-statements',
    name: 'Monthly Care Statements (Per Resident)',
    description: 'Individual resident care summary: assessments, care plan progress, activities, incidents, medication changes.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['residents', 'clinical', 'careplans', 'medication', 'incidents'],
    defaultColumns: ['resident_name', 'assessment_type', 'assessment_score', 'care_plan_goals', 'incident_type'],
    exportFormats: ['pdf'],
    icon: 'FileHeart',
    color: 'pink'
  },
  {
    id: 'monthly-facility-quality',
    name: 'Facility Quality Dashboard',
    description: 'Quality metrics: incident trends, complaint resolution, assessment compliance, star rating indicators.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['incidents', 'quality', 'clinical'],
    defaultColumns: ['nqip_indicator', 'nqip_value', 'star_rating'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Award',
    color: 'gold'
  },
  {
    id: 'monthly-financial-performance',
    name: 'Financial Performance',
    description: 'Revenue, expenses, AN-ACC subsidy, occupancy impact, budget variance by cost centre.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.FINANCIAL,
    dataSources: ['financial', 'facility'],
    defaultColumns: ['revenue', 'expenses'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'DollarSign',
    color: 'green'
  },
  {
    id: 'monthly-hr-compliance',
    name: 'HR Compliance',
    description: 'Mandatory training status, police check expiry, AHPRA registration, first aid, manual handling.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.HR,
    dataSources: ['roster'],
    defaultColumns: ['staff_name', 'staff_role'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Shield',
    color: 'blue'
  },
  {
    id: 'monthly-infection-control',
    name: 'Infection Control',
    description: 'Active infections, outbreak tracking, hand hygiene audits, PPE compliance, antibiotic usage.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['clinical', 'incidents', 'medication'],
    defaultColumns: ['resident_name', 'infection_type'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Bug',
    color: 'red'
  },
  {
    id: 'monthly-psychotropic',
    name: 'Psychotropic Medication',
    description: 'Residents on psychotropic medications: type, indication, review dates, consent status, behavioural supports tried.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['medication', 'residents', 'clinical'],
    defaultColumns: ['resident_name', 'psychotropic_medication', 'medication_dose'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Brain',
    color: 'purple'
  },
  {
    id: 'monthly-restrictive-practices',
    name: 'Restrictive Practices',
    description: 'All restrictive practices in use: type, authorisation, review schedule, alternatives considered.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.COMPLIANCE,
    dataSources: ['clinical', 'residents'],
    defaultColumns: ['resident_name', 'restrictive_practice_type'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Lock',
    color: 'red'
  },
  {
    id: 'monthly-complaints-feedback',
    name: 'Complaints & Feedback',
    description: 'All complaints and feedback received: category, resolution status, time to resolve, satisfaction ratings.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['quality'],
    defaultColumns: ['complaint_type', 'complaint_status'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'MessageSquare',
    color: 'orange'
  },

  // ─── QUARTERLY ────────────────────────────────────────
  {
    id: 'quarterly-nqip-review',
    name: 'NQIP Internal Review',
    description: 'National Quality Indicator Program: 10 indicator data collection, benchmarking, trend analysis.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.GOVERNMENT,
    dataSources: ['quality', 'incidents', 'clinical', 'medication'],
    defaultColumns: ['nqip_indicator', 'nqip_value'],
    exportFormats: ['pdf', 'excel', 'csv'],
    icon: 'BarChart3',
    color: 'blue'
  },
  {
    id: 'quarterly-cip-update',
    name: 'Continuous Improvement Plan Update',
    description: 'CIP progress: actions from audits, incidents, complaints, NQIP data, accreditation gaps.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['quality', 'incidents'],
    defaultColumns: [],
    exportFormats: ['pdf', 'excel'],
    icon: 'TrendingUp',
    color: 'green'
  },
  {
    id: 'quarterly-star-rating',
    name: 'Star Rating Self-Assessment',
    description: 'Internal star rating calculation across all sub-categories with gap analysis.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['quality', 'clinical', 'incidents', 'roster'],
    defaultColumns: ['star_rating'],
    exportFormats: ['pdf', 'excel'],
    icon: 'Star',
    color: 'gold'
  },
  {
    id: 'quarterly-antimicrobial',
    name: 'Antimicrobial Stewardship',
    description: 'Antibiotic prescribing patterns, duration, de-escalation, cultures ordered, resistance data.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.CLINICAL,
    dataSources: ['medication', 'clinical'],
    defaultColumns: ['resident_name', 'antimicrobial_name', 'medication_dose'],
    exportFormats: ['pdf', 'excel'],
    icon: 'TestTube',
    color: 'teal'
  },

  // ─── ANNUAL ───────────────────────────────────────────
  {
    id: 'annual-clinical-governance',
    name: 'Clinical Governance Report',
    description: 'Annual clinical governance: committee activities, clinical outcomes, risk management, research, credentialling.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['quality', 'clinical', 'incidents'],
    defaultColumns: [],
    exportFormats: ['pdf'],
    icon: 'BookOpen',
    color: 'indigo'
  },
  {
    id: 'annual-quality-consumers',
    name: 'Quality Report to Consumers',
    description: 'Public-facing annual quality report: performance, improvements, resident feedback, future plans.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.QUALITY,
    dataSources: ['quality', 'clinical'],
    defaultColumns: [],
    exportFormats: ['pdf'],
    icon: 'Users',
    color: 'green'
  },
  {
    id: 'annual-emergency-plan',
    name: 'Emergency Plan Review',
    description: 'Annual review of emergency and disaster management plan: drills conducted, gaps, updates needed.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.OPERATIONS,
    dataSources: ['facility'],
    defaultColumns: [],
    exportFormats: ['pdf'],
    icon: 'Siren',
    color: 'red'
  },
  {
    id: 'annual-internal-audit',
    name: 'Internal Audit Reports',
    description: 'All internal audits conducted: clinical, medication, infection control, documentation, WHS.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.COMPLIANCE,
    dataSources: ['quality'],
    defaultColumns: [],
    exportFormats: ['pdf', 'excel'],
    icon: 'ClipboardCheck',
    color: 'blue'
  },
  {
    id: 'annual-policy-review',
    name: 'Policy Review Register',
    description: 'Policy and procedure review status: overdue, upcoming, recently updated, version history.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.COMPLIANCE,
    dataSources: ['quality'],
    defaultColumns: [],
    exportFormats: ['pdf', 'excel'],
    icon: 'FileCheck',
    color: 'gray'
  },
  {
    id: 'annual-accreditation-pack',
    name: 'Accreditation Preparation Pack',
    description: 'Evidence portfolio for ACQSC accreditation across all 8 Quality Standards, 42 outcomes.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.COMPLIANCE,
    dataSources: ['quality', 'clinical', 'incidents', 'medication', 'roster', 'residents'],
    defaultColumns: [],
    exportFormats: ['pdf'],
    icon: 'Award',
    color: 'purple'
  }
];

export const GOVERNMENT_REPORT_TEMPLATES = [
  {
    id: 'gov-nqip-quarterly',
    name: 'NQIP Quarterly Submission',
    description: 'Auto-compiled NQIP data for Department of Health quarterly submission. All 10 quality indicators.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Department of Health and Aged Care',
    duePattern: 'Q+28 days',
    format: 'CSV (government template)',
    autoCompile: true,
    indicators: [
      'Pressure injuries', 'Physical restraint', 'Unplanned weight loss',
      'Falls and major injury', 'Medication management', 'Hospitalisations',
      'Workforce', 'Consumer experience', 'Quality of life', 'Continence'
    ]
  },
  {
    id: 'gov-qfr',
    name: 'Quarterly Financial Report (QFR)',
    description: 'Financial data via GPMS portal. Revenue, expenditure, staffing costs, care minutes financial impact.',
    frequency: REPORT_FREQUENCIES.QUARTERLY,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Department of Health and Aged Care',
    duePattern: 'Q+42 days',
    format: 'GPMS portal upload',
    autoCompile: true
  },
  {
    id: 'gov-acfr',
    name: 'Aged Care Financial Report (ACFR)',
    description: 'Annual financial statements, care minutes performance statement, prudential compliance statement. Due Oct 31.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Department of Health and Aged Care',
    dueDate: 'October 31',
    format: 'GPMS portal upload',
    autoCompile: true,
    sections: ['Financial Statements', 'Care Minutes Performance', 'Prudential Compliance']
  },
  {
    id: 'gov-provider-ops',
    name: 'Provider Operations Collection Form',
    description: 'Annual operational data: bed count, occupancy, staffing FTE, service types, resident demographics.',
    frequency: REPORT_FREQUENCIES.ANNUAL,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Department of Health and Aged Care',
    dueDate: 'As notified',
    format: 'Online form',
    autoCompile: true
  },
  {
    id: 'gov-care-minutes-monthly',
    name: 'Care Minutes Monthly Report',
    description: 'Actual care minutes delivered vs AN-ACC funded target, broken down by RN and total care staff.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Department of Health and Aged Care',
    duePattern: 'M+14 days',
    format: 'Internal + GPMS',
    autoCompile: true
  },
  {
    id: 'gov-rn-coverage',
    name: '24/7 RN Coverage Declaration',
    description: 'Monthly declaration that 24/7 registered nurse coverage was maintained. Auto-verified from roster data.',
    frequency: REPORT_FREQUENCIES.MONTHLY,
    category: REPORT_CATEGORIES.GOVERNMENT,
    regulatoryBody: 'Aged Care Quality and Safety Commission',
    duePattern: 'M+7 days',
    format: 'Declaration form',
    autoCompile: true
  }
];

export const ROLE_DEFAULT_DASHBOARDS = {
  facility_manager: {
    label: 'Facility Manager',
    tiles: [
      { id: 'occupancy', type: 'metric', title: 'Occupancy', source: 'daily-occupancy', size: 'sm' },
      { id: 'quality-score', type: 'metric', title: 'Quality Score', source: 'monthly-facility-quality', size: 'sm' },
      { id: 'financial', type: 'metric', title: 'Financial Position', source: 'monthly-financial-performance', size: 'sm' },
      { id: 'incidents', type: 'chart', title: 'Incident Trend', source: 'daily-incident-summary', size: 'md' },
      { id: 'care-minutes', type: 'chart', title: 'Care Minutes Compliance', source: 'daily-care-minutes', size: 'md' },
      { id: 'complaints', type: 'list', title: 'Open Complaints', source: 'monthly-complaints-feedback', size: 'md' },
      { id: 'star-rating', type: 'metric', title: 'Star Rating', source: 'quarterly-star-rating', size: 'sm' },
      { id: 'staffing', type: 'chart', title: 'Staffing Overview', source: 'weekly-staffing-adequacy', size: 'md' }
    ]
  },
  registered_nurse: {
    label: 'Registered Nurse',
    tiles: [
      { id: 'handover', type: 'list', title: 'Handover Items', source: 'daily-handover', size: 'lg' },
      { id: 'assessments-due', type: 'list', title: 'Assessments Due', source: 'weekly-clinical-review', size: 'md' },
      { id: 'care-plans', type: 'metric', title: 'Care Plan Reviews Due', source: 'monthly-care-statements', size: 'sm' },
      { id: 'medication', type: 'chart', title: 'Medication Rounds', source: 'daily-medication-admin', size: 'md' },
      { id: 'incidents-today', type: 'list', title: "Today's Incidents", source: 'daily-incident-summary', size: 'md' },
      { id: 'wound-status', type: 'list', title: 'Wound Management', source: 'weekly-clinical-review', size: 'md' }
    ]
  },
  admissions: {
    label: 'Admissions / CRM',
    tiles: [
      { id: 'pipeline', type: 'chart', title: 'CRM Pipeline', source: 'weekly-waitlist-status', size: 'lg' },
      { id: 'waitlist', type: 'list', title: 'Waitlist', source: 'weekly-waitlist-status', size: 'md' },
      { id: 'occupancy', type: 'metric', title: 'Bed Availability', source: 'daily-occupancy', size: 'sm' },
      { id: 'movements', type: 'list', title: 'Resident Movements', source: 'daily-resident-movement', size: 'md' },
      { id: 'enquiries', type: 'metric', title: 'New Enquiries (Week)', source: 'weekly-waitlist-status', size: 'sm' }
    ]
  },
  admin: {
    label: 'Administrator',
    tiles: [
      { id: 'occupancy', type: 'metric', title: 'Occupancy', source: 'daily-occupancy', size: 'sm' },
      { id: 'quality-score', type: 'metric', title: 'Quality Score', source: 'monthly-facility-quality', size: 'sm' },
      { id: 'financial', type: 'metric', title: 'Financial Position', source: 'monthly-financial-performance', size: 'sm' },
      { id: 'star-rating', type: 'metric', title: 'Star Rating', source: 'quarterly-star-rating', size: 'sm' },
      { id: 'incidents', type: 'chart', title: 'Incident Trend', source: 'daily-incident-summary', size: 'md' },
      { id: 'care-minutes', type: 'chart', title: 'Care Minutes', source: 'daily-care-minutes', size: 'md' },
      { id: 'staffing', type: 'chart', title: 'Staffing', source: 'weekly-staffing-adequacy', size: 'md' },
      { id: 'gov-reports', type: 'list', title: 'Government Reports Due', source: 'gov-nqip-quarterly', size: 'md' }
    ]
  }
};

export default PREBUILT_REPORTS;
